import m from '@bitstillery/mithril'
import {Dropdown, Icon, Link} from '@bitstillery/common/components/portal'
import {classes, present} from '@bitstillery/common/lib/utils'
import {MithrilComponent} from '@bitstillery/mithril'
import {$t} from '@bitstillery/common/app'

// @i18n ignore: product_category.**
import {$s} from '@/app'
import {filters} from '@/components/pages/offers/offer_list'
import {category_key} from '@/lib/category_key'
import {clear_shop_filters, select_shop_category, shop_category_route} from '@/lib/shop_category'
import {CmsFragment} from '@/cms/runtime/fragments'
import {page_copy, type PageCopyKind} from '@/cms/lib/page_meta'

const SHOP_ROUTE = '/shop'

/** Numeric segments (e.g. order ids) are shown as-is instead of being translated. */
function crumb_label(key: string) {
    return /^\d+$/.test(key) ? key : $t(`breadcrumbs.${key}`)
}

interface BreadcrumbsAttrs {
    /** Which copy family the surface uses; on `cms` the current crumb is the page's own title. */
    copy_kind?: PageCopyKind
    path?: string | undefined
}

export class Breadcrumbs extends MithrilComponent<BreadcrumbsAttrs> {
    /** Per-category open state, so a category's chevron can toggle its own sub-menu. */
    dropdown_state: Record<number, {toggle: boolean} | undefined> = {}

    view(vnode: m.Vnode<BreadcrumbsAttrs>) {
        const path = vnode.attrs.path ?? m.route.get()
        const segments = m.parsePathname(path).path.split('/').filter(Boolean)
        const crumbs = [
            {key: 'home', href: '/'},
            ...segments.map((part, index) => ({
                key: part.replace(/-/g, '_'),
                href: '/' + segments.slice(0, index + 1).join('/'),
            })),
        ]
        return (
            <div className='c-breadcrumbs'>
                <div className='main-navigation'>
                    {crumbs.slice(0, -1).map((crumb) => [
                        <Link className='breadcrumb' href={crumb.href} target='_self'>
                            {crumb_label(crumb.key)}
                        </Link>,
                        <Icon className='icon-d' name='chevronRight' />,
                    ])}
                    {crumbs.length > 0 &&
                        (() => {
                            const last = present(crumbs.at(-1), 'current crumb')
                            // The page being viewed names itself: a CMS page's title is copy it already
                            // owns, so the crumb follows a retitled page and survives a renamed path —
                            // where a `breadcrumbs.<slug>` key would silently start rendering itself.
                            // Ancestors keep the slug keys; their titles are not loaded, and the shop
                            // and my-portal have no page row to take a title from at all. The home page
                            // is the exception: it keeps the `breadcrumbs.home` label rather than its
                            // own page title.
                            const cms_title = vnode.attrs.copy_kind === 'cms' && last.href !== '/' ? page_copy('cms').title : ''
                            return [
                                // The shop's crumb is the one current crumb that still leads somewhere: the
                                // shop's state lives in its filters, not in its path, so "you are here" is a
                                // filtered view and the crumb is the way back out of it. Clicking it clears
                                // the filters through the same reset as the panel's clear control.
                                last.href === SHOP_ROUTE ? (
                                    <Link
                                        className='breadcrumb current'
                                        href={SHOP_ROUTE}
                                        onclick={() => clear_shop_filters()}
                                        target='_self'
                                    >
                                        {crumb_label(last.key)}
                                    </Link>
                                ) : (
                                    <span className='breadcrumb current'>{cms_title || crumb_label(last.key)}</span>
                                ),
                                crumbs.length === 1 && <Icon className='icon-d' name='chevronRight' />,
                            ]
                        })()}
                </div>
                {(() => {
                    const route = m.route.get()
                    // Product categories are only shown within the shop area
                    if (!$s.identity.token || !route.startsWith('/shop')) {
                        return null
                    }

                    const menu = $s.categories.menu || []
                    const selection: readonly (string | number)[] = filters.category.selection
                    return menu
                        .filter((menu_item) => (menu_item.priority ?? 0) > 0 && menu_item.number_of_products > 0)
                        .map((menu_item) => {
                            const is_selected = selection.includes(menu_item.artkey)
                            const has_active_child =
                                !is_selected && menu_item.children.map((i) => i.artkey).some((i: number) => selection.includes(i))
                            const label = $t(`product_category.${category_key(menu_item.name)}`)

                            // Leaf category — a single filter link, no sub-menu.
                            if (menu_item.children.length === 0) {
                                return (
                                    <Link
                                        active={is_selected}
                                        className='menu menu-leaf'
                                        href={shop_category_route(menu_item)}
                                        onclick={() => select_shop_category(menu_item)}
                                        tabindex={0}
                                        target='_self'
                                        type='unset'
                                    >
                                        {label}
                                    </Link>
                                )
                            }

                            // Parent category — the label filters the whole category; the chevron
                            // opens the sub-menu (on hover for pointers, on tap for touch). The two
                            // intents are separate elements, so a single interaction never does both.
                            const dropdown = (this.dropdown_state[menu_item.artkey] ??= {toggle: false})
                            return (
                                <Dropdown hover model={dropdown}>
                                    <div
                                        className={classes('menu menu-trigger', {
                                            active: is_selected,
                                            'active-child': has_active_child,
                                        })}
                                    >
                                        <Link
                                            active={is_selected}
                                            className='menu-label'
                                            href={shop_category_route(menu_item)}
                                            onclick={() => {
                                                dropdown.toggle = false
                                                select_shop_category(menu_item)
                                            }}
                                            tabindex={0}
                                            target='_self'
                                            type='unset'
                                        >
                                            {label}
                                        </Link>
                                        <button
                                            aria-expanded={dropdown.toggle}
                                            className='menu-chevron'
                                            onclick={(e: MouseEvent) => {
                                                e.stopPropagation()
                                                dropdown.toggle = !dropdown.toggle
                                            }}
                                        >
                                            <Icon name='chevronDown' />
                                        </button>
                                    </div>

                                    <div class='dropdown-body'>
                                        {menu_item.children
                                            .filter((submenu_item) => submenu_item.number_of_products > 0)
                                            .map((submenu_item) => (
                                                <Link
                                                    active={selection.includes(submenu_item.artkey) && !is_selected}
                                                    className='sub-menu'
                                                    href={shop_category_route(submenu_item)}
                                                    onclick={() => {
                                                        select_shop_category(submenu_item)
                                                    }}
                                                    tabindex={0}
                                                    target='_self'
                                                    type='unset'
                                                >
                                                    {$t(`product_category.${category_key(submenu_item.name)}`)}
                                                    <div class='count'>{submenu_item.number_of_products}</div>
                                                </Link>
                                            ))}
                                    </div>
                                </Dropdown>
                            )
                        })
                })()}
                {(() => {
                    // On CMS (non-shop) routes the slot beside the breadcrumbs — where the shop shows its
                    // category menu — is filled by a configurable fragment. Empty until a
                    // `cms-breadcrumb-menu` fragment is published (CmsFragment renders nothing when absent).
                    if (m.route.get().startsWith('/shop')) {
                        return null
                    }
                    return <CmsFragment name='cms-breadcrumb-menu' />
                })()}
            </div>
        )
    }
}
